import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { PageHeader, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { ClipboardList, CheckCircle2, ArrowRight, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function Queue() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [tab, setTab] = useState("today");

  const load = async () => {
    try {
      const [{ data: active }, { data: done }] = await Promise.all([
        api.get("/queue"),
        api.get("/queue?only_status=Completed"),
      ]);
      setItems(active || []);
      setCompleted(done || []);
    } catch { toast.error("Failed to load queue"); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (queue_id, status) => {
    try { await api.post(`/queue/${queue_id}/status`, { status }); toast.success(`Updated to ${status}`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const cancelVisit = async (q) => {
    const hasInvoice = !!q.consult_invoice_no;
    const msg = hasInvoice
      ? `Cancel ${q.patient_name}'s visit? The auto-generated invoice ${q.consult_invoice_no} will be voided.`
      : `Cancel ${q.patient_name}'s visit?`;
    if (!window.confirm(msg)) return;
    const reason = window.prompt("Reason for cancellation (optional):", "Patient did not present") || "";
    try {
      await api.post(`/queue/${q.queue_id}/status`, { status: "Cancelled", reason });
      toast.success(hasInvoice ? `Visit cancelled · invoice ${q.consult_invoice_no} voided` : "Visit cancelled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to cancel"); }
  };

  const rows = tab === "today" ? items : completed;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader title="Today's Queue" subtitle="Live waiting room view for reception and clinical staff." />

      <SectionCard
        title={
          <div className="flex items-center gap-1" data-testid="queue-tabs">
            <button data-testid="queue-tab-today" onClick={() => setTab("today")} className={`px-3 h-8 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === "today" ? "bg-[#1976D2] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Today's queue <span className={`text-[11px] tabular ${tab === "today" ? "text-white/80" : "text-slate-400"}`}>{items.length}</span>
            </button>
            <button data-testid="queue-tab-completed" onClick={() => setTab("completed")} className={`px-3 h-8 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${tab === "completed" ? "bg-[#2E7D32] text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              Completed visits <span className={`text-[11px] tabular ${tab === "completed" ? "text-white/80" : "text-slate-400"}`}>{completed.length}</span>
            </button>
          </div>
        }
        actions={<span className="text-xs text-slate-500">{tab === "today" ? `${items.length} active` : `${completed.length} completed today`}</span>}
      >
        {rows.length === 0 ? (
          <EmptyState icon={tab === "today" ? ClipboardList : CheckCircle2} title={tab === "today" ? "Queue is empty" : "No visits completed yet today"} description={tab === "today" ? "Newly registered patients and triaged walk-ins land here." : "Completed visits move here from the doctor's Complete button on the patient chart."} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Q#</th>
                  <th className="text-left px-4 py-2.5">Patient</th>
                  <th className="text-left px-4 py-2.5">Patient ID</th>
                  <th className="text-left px-4 py-2.5">Visit</th>
                  <th className="text-left px-4 py-2.5">{tab === "today" ? "Priority" : "Attending doctor"}</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                  <th className="text-left px-4 py-2.5">{tab === "today" ? "Waiting since" : "Completed at"}</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((q) => (
                  <tr key={q.queue_id} data-testid={`queue-row-${q.queue_id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 tabular font-semibold">#{q.queue_no}</td>
                    <td className="px-4 py-3">{q.patient_name}</td>
                    <td className="px-4 py-3 tabular">{q.patient_ths_id}</td>
                    <td className="px-4 py-3">{q.visit_type} · {q.department}</td>
                    <td className="px-4 py-3">
                      {tab === "today"
                        ? <StatusPill status={q.priority === "Urgent" ? "critical" : "normal"} />
                        : <span className="text-slate-600">{q.assigned_doctor_name || "—"}</span>}
                    </td>
                    <td className="px-4 py-3"><StatusPill status={q.status} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500 tabular">
                      {tab === "today"
                        ? new Date(q.created_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
                        : (q.completed_at ? new Date(q.completed_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—")}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => navigate(`/app/patients/${q.patient_id}`)} data-testid={`q-open-${q.queue_id}`} className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium flex items-center gap-1">
                          Open <ArrowRight className="w-3 h-3" />
                        </button>
                        {tab === "today" && (
                          <>
                            <button onClick={() => setStatus(q.queue_id, "In Consultation")} data-testid={`q-consult-${q.queue_id}`} className="text-xs px-2 py-1 rounded-md bg-[#e6f0fb] text-[#1565C0] font-medium">In consult</button>
                            <button onClick={() => setStatus(q.queue_id, "Completed")} data-testid={`q-complete-${q.queue_id}`} className="text-xs px-2 py-1 rounded-md bg-[#e6f4ea] text-[#1B5E20] font-medium">Complete</button>
                            <button onClick={() => cancelVisit(q)} data-testid={`q-cancel-${q.queue_id}`} title={q.consult_invoice_no ? `Void invoice ${q.consult_invoice_no}` : "Cancel visit"} className="text-xs px-2 py-1 rounded-md border border-red-200 bg-white text-[#B71C1C] font-medium flex items-center gap-1 hover:bg-red-50"><XCircle className="w-3 h-3" /> Cancel</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
